import { pedeReajuste } from '@favo/core';
import type { Cenario, Parametros, Recomendacao } from '@favo/core';
import type * as React from 'react';

import { Celula } from '@/componentes/celula';
import { TabelaCenarios, type LinhaCenario } from '@/componentes/tabela-cenarios';
import { Valor } from '@/componentes/valor';
import { formatarMoeda, formatarPercentualPontos } from '@/design/formato';

import { faltaPreencher } from './frases';
import { formatarVolume, tomDoLucro } from './grandezas';
import type { Necessario } from './volume-necessario';

/** Projeção anual: a coluna do que é hoje e, quando ela diz algo, a de comparação. */
export function Comparativo({
  p,
  rec,
  atual,
  necessario,
}: {
  p: Parametros;
  rec: Recomendacao;
  atual: Cenario | null;
  necessario: Necessario;
}) {
  if (!atual) {
    return (
      <Celula como="section" className="secao">
        <h2 className="titulo-cartao titulo-secao">Projeção anual</h2>
        <p className="apoio">{faltaPreencher(p)}</p>
      </Celula>
    );
  }

  /* A coluna de comparação é UMA só, e o estado escolhe qual:
   *   prejuízo e abaixo → o mesmo volume com o preço no mínimo. É a
   *                       recomendação — em prejuízo, mais ainda: sugerir
   *                       volume ao preço atual seria vender mais para perder
   *                       mais.
   *   os demais         → o volume necessário, sempre ao preço ATUAL.
   * No estado 'acima' é isso que impede um preço menor de existir na tabela:
   * não há coluna de preço, e a de volume usa o preço praticado. */
  const comparacao =
    pedeReajuste(rec)
      ? {
          titulo: 'Com o preço no mínimo',
          preco: rec.precoSugerido,
          volume: p.V12,
          cen: rec.sugerido,
        }
      : // Coluna de volume só quando ela DIZ algo: volume necessário igual ao
        // atual repetiria a coluna "Atual" célula por célula.
        necessario.tipo === 'ok' && Math.abs(necessario.volume - p.V12) >= 1
        ? {
            titulo: 'No volume necessário',
            preco: p.ticket,
            volume: necessario.volume,
            cen: necessario.cen,
          }
        : null;

  const par = (atualNo: React.ReactNode, comparado: (c: Cenario) => React.ReactNode) => ({
    atual: atualNo,
    ...(comparacao ? { sugerido: comparado(comparacao.cen) } : {}),
  });

  // Delta só onde a coluna é uma RECOMENDAÇÃO a seguir. Na coluna de volume
  // necessário ele seria enganoso: aquele volume é o PISO da meta, não um alvo,
  // e "−R$ 460 mil no ano" leria como advertência contra vender mais.
  const delta = comparacao && pedeReajuste(rec) ? deltaLucro(atual, comparacao.cen) : null;

  const linhas: LinhaCenario[] = [
    {
      metrica: 'Preço de venda',
      atual: <Valor valor={p.ticket} formato="moeda" />,
      ...(comparacao ? { sugerido: <Valor valor={comparacao.preco} formato="moeda" /> } : {}),
    },
    {
      metrica: 'Volume anual',
      nota: 'projetos por ano',
      atual: <span className="numero">{formatarVolume(p.V12)}</span>,
      ...(comparacao
        ? { sugerido: <span className="numero">{formatarVolume(comparacao.volume)}</span> }
        : {}),
    },
    {
      metrica: 'Faturamento anual',
      ...par(<Valor valor={atual.anual.faturamento} formato="moeda" />, (c) => (
        <Valor valor={c.anual.faturamento} formato="moeda" />
      )),
    },
    {
      metrica: 'Margem de contribuição no ano',
      ...par(<Valor valor={atual.anual.mc} formato="moeda" />, (c) => (
        <Valor valor={c.anual.mc} formato="moeda" />
      )),
    },
    {
      metrica: 'Margem de contribuição',
      nota: 'sobre o preço',
      ...par(<Valor valor={atual.mcPct} formato="percentual" />, (c) => (
        <Valor valor={c.mcPct} formato="percentual" />
      )),
    },
    {
      metrica: 'Despesa fixa anual',
      ...par(<Valor valor={atual.anual.despFixa} formato="moeda" />, (c) => (
        <Valor valor={c.anual.despFixa} formato="moeda" />
      )),
    },
    {
      metrica: 'Lucro anual',
      ...par(
        <Valor valor={atual.anual.lucro} formato="moeda" tom={tomDoLucro(atual.anual.lucro)} />,
        (c) => <Valor valor={c.anual.lucro} formato="moeda" tom={tomDoLucro(c.anual.lucro)} />,
      ),
      ...(delta === null
        ? {}
        : {
            delta: `${delta < 0 ? '' : '+'}${formatarMoeda(delta)} no ano`,
            tomDelta: delta < 0 ? ('negativo' as const) : ('positivo' as const),
          }),
    },
    {
      metrica: 'Margem líquida',
      nota: 'sobre o preço',
      ...par(
        <Valor valor={atual.lucroPct} formato="percentual" tom={tomDoLucro(atual.lucroPct)} />,
        (c) => <Valor valor={c.lucroPct} formato="percentual" tom={tomDoLucro(c.lucroPct)} />,
      ),
    },
  ];

  return (
    <Celula como="section" className="secao">
      <h2 className="titulo-cartao titulo-secao">
        {comparacao
          ? `Projeção anual: atual e ${comparacao.titulo.toLowerCase()}`
          : 'Projeção anual'}
      </h2>
      <TabelaCenarios
        linhas={linhas}
        colunaAtual="Atual"
        legenda={
          comparacao
            ? `Projeção anual da operação: cenário atual e ${comparacao.titulo.toLowerCase()}.`
            : 'Projeção anual da operação no cenário atual.'
        }
        {...(comparacao ? { colunaSugerido: comparacao.titulo } : {})}
        rodape={rodapeComparativo(p, rec, necessario)}
      />
    </Celula>
  );
}

function deltaLucro(atual: Cenario, outro: Cenario): number | null {
  if (atual.anual.lucro === null || outro.anual.lucro === null) return null;
  return outro.anual.lucro - atual.anual.lucro;
}

function rodapeComparativo(
  p: Parametros,
  rec: Recomendacao,
  necessario: Necessario,
): React.ReactNode {
  if (necessario.tipo === 'indisponivel') return necessario.motivo;
  // No estado 'abaixo' a coluna mostra o preço; o volume necessário vira uma
  // linha de prosa para não sumir do documento.
  if (rec.estado === 'abaixo') {
    return `Ao preço atual, a meta exigiria ${formatarVolume(necessario.volume)} projetos por ano contra os ${formatarVolume(p.V12)} de hoje.`;
  }
  if (Math.abs(necessario.volume - p.V12) < 1) {
    return `O volume de hoje, ${formatarVolume(p.V12)} projetos por ano, é exatamente o que a meta de ${formatarPercentualPontos(p.lucroAlvo)} pede ao preço praticado.`;
  }
  return `O volume necessário mantém o preço praticado: é quantos projetos por ano diluem a despesa fixa até a meta de ${formatarPercentualPontos(p.lucroAlvo)}.`;
}
